/**
 * Prophets Hub — feature flags.
 *
 * A single, centralised place to enable/disable Hub modules without
 * touching feature code, routes, database collections, or admin
 * endpoints. Every hide-a-feature decision in the app should read from
 * this file so re-enabling a feature is one line, not a code archaeology
 * project.
 *
 * When a flag is `false`:
 *   • The public Hub nav item + mobile menu row is not rendered.
 *   • The corresponding /hub/... route redirects to /hub.
 *   • The Hub Admin tab is hidden and its inner subroute redirects to
 *     the default admin section.
 *   • Any user-facing history/profile section for the feature is hidden.
 *   • Backend code, DB collections, admin API endpoints, and existing
 *     records are NOT touched — flipping the flag back to `true`
 *     restores the feature immediately with all data intact.
 *
 * Do NOT scatter `display: none` or ad-hoc route checks around the
 * codebase. Add them here.
 */

export const HUB_FEATURE_FLAGS = Object.freeze({
  // Reversible temporary disable — 2026-07-25. Restore by setting to true.
  // Backend routes / DB collections / admin API remain fully functional.
  HUB_PROPHECY_ENABLED: false,
  // Reversible temporary disable — 2026-02-17. Restore by setting to true.
  // Backend quiz routes, DB collections, and admin controls all remain
  // fully functional; flipping this back to true immediately restores
  // the public UI with all data intact.
  HUB_QUIZ_ENABLED: false,
  // Reversible temporary disable — 2026-06 (pre-deployment). CASINO is
  // unfinished, so the nav entry is hidden and /hub/casino (plus any
  // nested route) redirects to /hub with history replacement. All Casino
  // code, components, assets, backend routes, DB collections and admin
  // controls remain untouched — flip this to true to restore everything.
  HUB_CASINO_ENABLED: false,
  // Preview-only IA reorganization (Dashboard/Earn/Predictions/Marketplace/Profile).
  // Flip to false to instantly restore the flat legacy nav. Backend untouched.
  HUB_NEW_NAV: true,
  // Slice S10 — admin IA reorganization. When true the admin sidebar
  // shows Transmissions + Marketplace as top-level items, Prediction
  // becomes a parent with Polymarket/NFT sub-tabs, and legacy
  // `/admin/hub?tab=…` URLs auto-redirect to the new locations.
  // Flip to false to restore the pre-S10 flat admin layout instantly.
  // Zero backend/DB changes are tied to this flag.
  ADMIN_NEW_IA: true,
});

export function isFeatureEnabled(key) {
  return Boolean(HUB_FEATURE_FLAGS[key]);
}
